import { create } from "zustand";
import { persist } from "zustand/middleware";

/** この期間を過ぎた値は問い合わせ画面に表示しない(無関係な訪問への持ち越しを防ぐ) */
const PURCHASE_ISSUE_TTL_MS = 24 * 60 * 60 * 1000;

type PurchaseIssue = {
  internalTransactionId: string | null;
  /** Jambo-Server決済確定API(confirmPurchaseV2)のレスポンスコード。Apple/StoreKit側で失敗した場合は常にnull */
  errorCode: number | null;
  /** Apple/StoreKit側のエラー種別。`errorCode`がnullの場合に原因を辿る手がかりになる */
  clientFailureCode: string | null;
  /** 決済フローのどこまで進んで失敗したか */
  stage: string | null;
  /** 失敗発生日時(ISO8601) */
  occurredAt: string | null;
};

type PurchaseIssueState = PurchaseIssue & {
  createdAt: number | null;
  setPurchaseIssue: (issue: Partial<PurchaseIssue>) => void;
  clearPurchaseIssue: () => void;
};

const emptyIssue: PurchaseIssue = {
  internalTransactionId: null,
  errorCode: null,
  clientFailureCode: null,
  stage: null,
  occurredAt: null,
};

/**
 * Carinaの決済失敗系イベント(purchaseFailed/purchaseAlreadyProcessed/purchaseStatusUnknown)から
 * 受け取った決済失敗情報を、問い合わせページ(ContactTemplate)遷移まで保持する。
 * 決済失敗からお問い合わせ画面遷移までの間にWebViewがリロードされるケースを考慮し、localStorageに永続化する。
 *
 * 「問い合わせページを開いた」を「問い合わせが完了した」とみなして即クリアする実装は誤り
 * (外部iframeフォームのため送信完了を検知できず、開いただけで離脱→再訪時に情報が消えるバグになる)。
 * 代わりにTTLで期限切れの値のみ無視する方式にする。
 */
export const usePurchaseIssueStore = create<PurchaseIssueState>()(
  persist(
    (set) => ({
      ...emptyIssue,
      createdAt: null,
      setPurchaseIssue: (issue) =>
        set({
          ...emptyIssue,
          ...issue,
          createdAt: Date.now(),
        }),
      clearPurchaseIssue: () => set({ ...emptyIssue, createdAt: null }),
    }),
    {
      name: "purchase-issue-storage",
    },
  ),
);

/** TTL内であれば保持中の値を返す。期限切れ・未設定ならnull。 */
export const getActivePurchaseIssue = (): PurchaseIssue | null => {
  const {
    internalTransactionId,
    errorCode,
    clientFailureCode,
    stage,
    occurredAt,
    createdAt,
  } = usePurchaseIssueStore.getState();
  if (createdAt === null || Date.now() - createdAt > PURCHASE_ISSUE_TTL_MS) {
    return null;
  }
  return {
    internalTransactionId,
    errorCode,
    clientFailureCode,
    stage,
    occurredAt,
  };
};
